<template>
  <v-container grid-list-md>
    <v-row>
      <v-col cols="12" md="10" offset-md="1" lg="8" offset-lg="2">
        <v-card v-if="showHeader || showImage || description">
          <v-toolbar v-if="showHeader">
            <v-toolbar-title>{{ name }}</v-toolbar-title>
          </v-toolbar>
          <v-img v-if="showImage" :src="`/img/theatre/${showId}.jpg`" height="300">
          </v-img>
          <v-card-text v-if="description" class="theatre-event-description" v-html="description" />
          <v-card-actions v-if="!isLoggedIn && !activeDiscount._id">
            <v-dialog v-model="discountDialog" width="400">
              <template #activator="{ on }">
                <v-btn v-on="on" text>Enter Discount Code</v-btn>
              </template>
              <v-card>
                <v-card-title>Enter Discount Code</v-card-title>
                <v-card-text>
                  <p>Enter the discount code and associated email address below to enable the discount option.</p>
                  <v-text-field v-model="discountEmail" label="Discount Email" outlined />
                  <v-text-field v-model="discountCode" label="Discount Code" outlined />
                </v-card-text>
                <v-card-actions>
                  <v-btn text @click="loadDiscount">Lookup Discount</v-btn>
                  <v-spacer />
                  <v-btn text @click="discountDialog = false">Cancel</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-card-actions>
        </v-card>
        <v-card v-if="purchaseComplete">
          <v-card-title>{{ paymentTotal === 0 ? 'Reservation' : 'Purchase' }} Complete</v-card-title>
          <v-card-text>
            <p v-if="paymentTotal > 0">Thank you for purchasing your tickets to {{ name }}. Below is a summary of your purchase. Please see your email for receipt of your payment from PayPal, plus a copy of this summary. If you need to make changes, please reply to that email.</p>
            <p v-else>Thank you for reserving your tickets for {{ name }}. Below is a summary of the tickets you've reserved, which will also be emailed to you. If you need to cancel or make changes to your reservations, please respond to that email to let us know so we can update your reservation and make room for other guests.</p>
            <h3>Ticket Summary:</h3>
            <v-list>
              <v-list-item v-for="({ date, label, tickets, price, total }, index) of tickets" :key="index">
                <v-list-item-content>
                  <v-list-item-title>{{ label }}</v-list-item-title>
                  <v-list-item-subtitle>{{ date }}</v-list-item-subtitle>
                  <v-list-item-subtitle>{{ tickets }} Ticket{{ tickets === 1 ? '' : 's' }} for ${{ price }} {{ tickets > 1 ? 'each' : '' }}</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>${{ total }}</v-list-item-action>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
        <template v-else>
          <v-card class="mt-2">
            <v-card-text>
              <h2>Performances</h2>
              <p>Please choose a performance below to purchase your tickets for that performance.</p>
              <v-list>
                <purchase-expander
                  v-for="perf of performances"
                  :key="'perf-' + perf._id"
                  :show-id="showId"
                  :name="name"
                  :performance="perf"
                  :is-admin="isAdmin"
                  @update="(upd) => updateTickets(perf, upd)" />
              </v-list>
            </v-card-text>
          </v-card>
          <v-card v-if="ticketCount > 0" class="mb-6">
            <v-card-text>
              <p v-if="isLoggedIn">Since you are logged in, this purchase will be linked to your Covenant account:<br/>Name: {{ user.preferred }} {{ user.last }}<br/>Email: {{ user.email }}</p>
              <v-row v-else>
                <v-col cols="12" class="pb-0">
                  <p>Please complete this form to purchase your tickets.</p>
                  <p>You are currently not logged in. If you are an employee or student and want to see these options, please log in.</p>
                </v-col>
                <v-col>
                  <v-text-field v-model="first" label="First Name" outlined hide-details />
                </v-col>
                <v-col>
                  <v-text-field v-model="last" label="Last Name" outlined hide-details />
                </v-col>
                <v-col cols="12">
                  <v-text-field v-model="email" label="Email Address" outlined />
                </v-col>
              </v-row>
            </v-card-text>
            <v-card-actions>
              <v-btn :disabled="!(isLoggedIn || (first && last && email))" @click="saveTickets()">{{ paymentTotal > 0 ? `Pay $${paymentTotal} with PayPal` : 'Submit Reservation' }}</v-btn>
              <v-spacer />
              <v-dialog v-model="previewDialog" width="400">
                <template #activator="{ on }">
                  <v-btn v-on="on">View Ticket Summary</v-btn>
                </template>
                <v-card>
                  <v-card-title>Ticket Summary</v-card-title>
                  <v-card-text>
                    <v-list>
                      <v-list-item v-for="({ date, label, tickets, price, total }, index) of tickets" :key="index">
                        <v-list-item-content>
                          <v-list-item-title>{{ label }}</v-list-item-title>
                          <v-list-item-subtitle>{{ date }}</v-list-item-subtitle>
                          <v-list-item-subtitle>{{ tickets }} Ticket{{ tickets === 1 ? '' : 's' }} for ${{ price }} {{ tickets > 1 ? 'each' : '' }}</v-list-item-subtitle>
                        </v-list-item-content>
                        <v-list-item-action>${{ total }}</v-list-item-action>
                      </v-list-item>
                    </v-list>
                  </v-card-text>
                  <v-card-actions>
                    <v-btn @click="previewDialog = false">Close</v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </v-card-actions>
            <payment-popup ref="paymentPopup" :total="paymentTotal" :items="paymentItems" :show-activator="false" department="Theatre" :foapal="{ fund: '9999', org: '9999', acct: '99999', prog: '99' }" @success="paymentSuccessful" @cancel="cancelTickets" />
          </v-card>
        </template>
      </v-col>
    </v-row>
  </v-container>
</template>
<style>
div.theatre-event-description h1, div.theatre-event-description h2, div.theatre-event-description h3 {
  margin-bottom: .3em;
}
</style>
<script>
import { ref, computed, onMounted } from '@vue/composition-api'

export default {
  components: {
    PaymentPopup: () => import('@/components/core/PaymentPopup'),
    PurchaseExpander: () => import('@/components/theatre/purchaseExpander')
  },
  setup (props, { root }) {
    const user = computed(() => 'spoof' in root.$store.state.user ? root.$store.state.user.spoof : root.$store.state.user)
    const isLoggedIn = computed(() => root.$store.state.loggedIn)
    const isAdmin = computed(() => isLoggedIn.value && user.value.roles.includes('Technology Services'))
    const showId = ref('')
    const shortName = computed(() => root.$route.params.show)
    const showHeader = ref(true)
    const name = ref('')
    const showImage = ref(false)
    const description = ref('')
    const performances = ref([])
    const activeDiscount = ref({
      _id: '',
      code: '',
      email: '',
      discountedPrice: 0,
      limit: 0,
      used: 0,
      performances: []
    })
    const discountDialog = ref(false)
    const discountCode = ref('')
    const discountEmail = ref('')
    async function loadDiscount () {
      // Do a lookup to see if this discount code and email exist
      const aggregate = [
        { $match: { active: true, shortName: shortName.value } },
        // This will pre-filter the discounts list to only include one that matches the current user's email
        { $addFields: {
          discounts: { $filter: {
            input: '$discounts',
            as: 'item',
            cond: { $and: [
              { $eq: ['$$item.code', discountCode.value] },
              { $eq: ['$$item.email', discountEmail.value] },
              { $in: ['$$item.used', [false, null]] }
            ] }
          } }
        } },
        // This will turn it into a single field, or will hide the field if there is no matching discount entry
        { $addFields: {
          discounts: { $first: '$discounts' }
        } },
        { $limit: 1 }
      ]
      const { data } = await root.$feathers.service('theatre/show').find({ query: { aggregate } })
      if (data.length === 0 || !('discounts' in data[0])) {
        alert('No discount code was found matching your entry. Please check the email you received and make sure both the code and email address match the information (only a Covenant email address will receive a code).')
      } else {
        activeDiscount.value = data[0].discounts
        for (let i = 0; i < performances.value.length; i++) {
          if (activeDiscount.value.performances.includes(performances.value[i]._id)) {
            const prices = [ ...performances.value[i].prices ]
            prices.splice(0, 0, { label: 'Discounted Ticket', price: activeDiscount.value.discountedPrice, limit: activeDiscount.value.limit })
            performances.value.splice(i, 1, { ...performances.value[i], prices })
          }
        }
        root.$store.dispatch('main/snackbar', { color: 'success', text: 'Discount code loaded successfully' })
        discountDialog.value = false
      }
    }

    const isLoaded = ref(false)

    onMounted(async () => {
      const aggregate = [
        { $match: { active: true, shortName: shortName.value } },
        // This will pre-filter the discounts list to only include one that matches the current user's email
        { $addFields: {
          discounts: { $filter: {
            input: '$discounts',
            as: 'item',
            cond: { $and: [
              { $eq: ['$$item.email', user.value.email || 'invalid@'] },
              { $or: [
                { $eq: ['$$item.used', false] },
                { $eq: ['$$item.used', null] }
              ] }
            ] }
          } }
        } },
        // This will turn it into a single field, or will hide the field if there is no matching discount entry
        { $addFields: {
          discounts: { $first: '$discounts' }
        } },
        { $limit: 1 }
      ]
      const { data } = await root.$feathers.service('theatre/show').find({ query: { aggregate } })
      if (data.length === 0) {
        root.$dispatch('main/snackbar', { color: 'error', text: 'The show you attempted to view was not found' })
        root.$router.replace('/theatre')
        return
      }
      showId.value = data[0]._id
      showHeader.value = data[0].showHeader
      name.value = data[0].name
      showImage.value = data[0].image == null || data[0].image
      description.value = data[0].description
      const parser = new DOMParser()
      const doc = parser.parseFromString(data[0].description, 'text/html')
      description.value = ((doc.body.textContent || '') === '') ? '' : data[0].description
      if (isLoggedIn.value && 'discounts' in data[0]) {
        activeDiscount.value = data[0].discounts
      }
      performances.value = data[0].performances.sort((a, b) => new Date(a.date) < new Date(b.date) ? -1 : 1).map((row) => {
        const dt = new Date(row.date)
        const date = dt.toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'long', day: 'numeric' })
        const time = dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' })
        row.dateStr = `${date} at ${time}`
        if (activeDiscount.value.performances.includes(row._id)) {
          row.prices.splice(0, 0, {
            label: 'Discounted Ticket',
            price: activeDiscount.value.discountedPrice,
            limit: activeDiscount.value.limit
          })
        }
        return row
      })
      isLoaded.value = true
    })

    const tickets = ref([])
    const ticketCount = computed(() => tickets.value.reduce((prev, { tickets }) => prev + tickets, 0))
    const first = ref('')
    const last = ref('')
    const email = ref('')
    function updateTickets (performance, { label, count }) {
      const index = tickets.value.findIndex(({ performance: perfId, label: l }) => perfId === performance._id && label === l)
      if (index >= 0) {
        if (count === 0) tickets.value.splice(index, 1)
        else tickets.value.splice(index, 1, { ...tickets.value[index], tickets: count, total: tickets.value[index].price * count })
      } else {
        const { price } = performance.prices.find(({ label: l }) => label === l)
        tickets.value.push({ performance: performance._id, date: new Date(performance.date).toLocaleString(), label, tickets: count, price, total: price * count })
      }
      if (label === 'Discounted Ticket') {
        activeDiscount.value.used = tickets.value.reduce((prev, { label, tickets }) => prev + (label === 'Discounted Ticket' ? tickets : 0), 0)
        // Update the limit on all of the other performances to reflect the new limit based on how many have been used
        for (let i = 0; i < performances.value.length; i++) {
          // Set the limit on each one to be the allowed limit - the used tickets by the other performances
          const prices = [ ...performances.value[i].prices ]
          const priceObj = prices.find(({ label }) => label === 'Discounted Ticket')
          if (priceObj) {
            // Get the number of tickets used by all of the other performances (not the current one)
            const used = tickets.value.reduce((prev, { performance: perfId, label, tickets }) => prev + (perfId !== performances.value[i]._id && label === 'Discounted Ticket' ? tickets : 0), 0)
            priceObj.limit = activeDiscount.value.limit - used
            performances.value.splice(i, 1, { ...performances.value[i], prices })
          }
        }
      }
    }
    const paymentTotal = computed(() => tickets.value.reduce((prev, { total }) => prev + total, 0))

    const paymentPopup = ref(null)
    const paymentItems = computed(() => tickets.value.map(({ date, label, tickets, price }) => {
      return { name: `${name.value} on ${date}: ${label}`, quantity: tickets, unit_amount: { currency_code: 'USD', value: price } }
    }))
    const purchaseComplete = ref(false)
    const purchaseDetails = ref({
      paypalId: '',
      payerName: '',
      payerEmail: '',
      amount: '',
      items: []
    })
    async function paymentSuccessful ({ logId, paypalId }) {
      console.log('Payment was successful')
      // Update the saved tickets record to show that it's been paid
      if (ticketId.value) await root.$feathers.service('theatre/ticket').patch(ticketId.value, { status: 'Paid' })
      // Load the payment information so we can tell them that the payment was successful
      purchaseDetails.value = await root.$feathers.service('system/paypal-log').get(logId)
      purchaseComplete.value = true
    }

    const ticketId = ref(null)
    async function saveTickets () {
      // Save the tickets as a pending record, just in case the payment gets disconnected.
      if (ticketId.value) {
        await root.$feathers.service('theatre/ticket').patch(ticketId.value, {
          first: isLoggedIn.value ? user.value.first : first.value,
          last: isLoggedIn.value ? user.value.last : last.value,
          email: isLoggedIn.value ? user.value.email : email.value,
          tickets: tickets.value,
          status: 'Reserved'
        })
      } else {
        const res = await root.$feathers.service('theatre/ticket').create({
          first: isLoggedIn.value ? user.value.preferred : first.value,
          last: isLoggedIn.value ? user.value.last : last.value,
          email: isLoggedIn.value ? user.value.email : email.value,
          bannerId: isLoggedIn.value ? user.value.bannerId : null,
          show: showId.value,
          tickets: tickets.value
        })
        if (res) ticketId.value = res._id
      }
      const $inc = {}
      let index = 0
      const arrayFilters = []
      const idHash = {}
      tickets.value.forEach(({ performance, tickets }) => {
        if (performance in idHash) {
          $inc[`performances.$[perf${idHash[performance]}].reserved`] += tickets
        } else {
          $inc[`performances.$[perf${index}].reserved`] = tickets
          arrayFilters.push({ [`perf${index}._id`]: performance })
          idHash[performance] = index
          index++
        }
      })
      await root.$feathers.service('theatre/show').patch(showId.value, { $inc }, { query: { arrayFilters } })
      // We can use a script to load payment records from PayPal and find ones that aren't in the log
      // and match them up to these saved records and mark them as paid.
      if (paymentTotal.value === 0) {
        if (ticketId.value) await root.$feathers.service('theatre/ticket').patch(ticketId.value, { status: 'Paid' })
        const to = isLoggedIn.value ? user.value.email : email.value
        // ToDo: Build a nice, email-formatted view of the ticket summary
        await root.$feathers.service('system/email').create({ from: 'report-email@covenant.edu', to, subject: 'Ticket Reservation Confirmation for ' + name.value, html: `<p>Thank you for reserving tickets to ${name.value}. Below is a list of the tickets that you've reserved. If your plans change and you no longer need any of these tickets, please reply to this email so we can free up those tickets for other guests.<p><p><b>Ticket Summary:</b></p>` })
      } else {
        paymentPopup.value.activate()
      }
    }

    async function cancelTickets () {
      await root.$feathers.service('theatre/ticket').patch(ticketId.value, { status: 'Cancelled' })
      const $inc = {}
      let index = 0
      const arrayFilters = []
      const idHash = {}
      tickets.value.forEach(({ performance, tickets }) => {
        if (idHash[performance]) {
          $inc[`performances.$[perf${idHash[performance]}].reserved`] = tickets * -1
        } else {
          $inc[`performances.$[perf${index}].reserved`] = tickets * -1
          arrayFilters.push({ [`perf${index}._id`]: performance })
          idHash[performance] = index
          index++
        }
      })
      await root.$feathers.service('theatre/show').patch(showId.value, { $inc }, { query: { arrayFilters } })
    }

    const previewDialog = ref(false)

    return {
      user,
      isLoggedIn,
      isAdmin,
      showId,
      shortName,
      showHeader,
      name,
      showImage,
      description,
      performances,
      activeDiscount,
      discountDialog,
      discountCode,
      discountEmail,
      loadDiscount,
      tickets,
      ticketCount,
      first,
      last,
      email,
      updateTickets,
      paymentTotal,
      paymentPopup,
      paymentItems,
      purchaseComplete,
      purchaseDetails,
      paymentSuccessful,
      saveTickets,
      cancelTickets,
      previewDialog
    }
  }
}
</script>
